import {
  SegmentAIConditionRequest,
  SegmentAIConditionResponse,
  SegmentAIProfileQuestion,
  AIProfileResponse,
  Constraint,
  ConstraintSimple,
  GetSegmentsRequest,
  GetSegmentsResponse,
  SegmentCountResponse,
  SegmentCreateRequest,
  SegmentQueryDataResponse,
  SegmentResponse,
  SegmentSimple,
  SegmentUpdateRequest,
} from "types";
import {
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { SegmentsAPI } from "api";
import { formatConstraints } from "utils";
import { mapBy } from "utils/base";

export type UseSegmentConstraintsArgs = {
  query: string;
  uuid: string;
};

const queryKeys = {
  get: (id: string) => [id] as const,
  all: () => ["segments"] as const,
  lists: () => [...queryKeys.all(), "list"] as const,
  listAll: () => [...queryKeys.lists(), "all"] as const,
  listFavorites: () => [...queryKeys.lists(), "favorites"] as const,
  listConstraints: (args: UseSegmentConstraintsArgs) => [...queryKeys.lists(), args] as const,
  listGeos: (uuid: string) => [...queryKeys.lists(), "geos", uuid] as const,
  aiProfile: (uuid: string, questions: SegmentAIProfileQuestion[], customInstructions?: string) =>
    [...queryKeys.all(), "ai-profile", uuid, questions, customInstructions] as const,
  query: (args: GetSegmentsRequest) => [args] as const /** TODO: normalize with "lists" key */,
  constraintCount: (constraints: Constraint[]) => ["segment-constraint-count", constraints] as const,
};

export const useSegment = <T = SegmentResponse>(
  id: string,
  options: UseQueryOptions<SegmentResponse, unknown, T> = {},
) => useQuery<SegmentResponse, unknown, T>(queryKeys.get(id), () => SegmentsAPI.get(id), options);

/**
 * Fetches the respondent count for a given set of (unformatted) constraints. Shared by the
 * segment header's count and the per-constraint-row counts so identical constraint sets dedupe
 * to a single network call.
 *
 * Formats the constraints inside the queryFn rather than at the call site -- formatConstraints
 * can throw (e.g. a SEG constraint missing a uuid), and letting react-query invoke it means a
 * throw becomes a normal query error instead of crashing whatever called this during render.
 */
export const useSegmentConstraintCount = <T = SegmentCountResponse>(
  constraints: Constraint[],
  options: UseQueryOptions<SegmentCountResponse, unknown, T> = {},
) =>
  useQuery<SegmentCountResponse, unknown, T>(
    queryKeys.constraintCount(constraints),
    () => SegmentsAPI.getSampleConditionCount({ constraints: formatConstraints(constraints) }),
    options,
  );

/**
 * Fetches a list of SegmentSimple instances - typically used within a select search.
 */
export const useAllSegments = <T = SegmentSimple[]>(options: UseQueryOptions<SegmentSimple[], unknown, T> = {}) =>
  useQuery<SegmentSimple[], unknown, T>(queryKeys.listAll(), SegmentsAPI.getAll, options);

/**
 * Fetches a list of favorited SegmentSimple instances - typically used within a select search.
 */
export const useSegmentFavorites = <T = SegmentSimple[]>(options: UseQueryOptions<SegmentSimple[], unknown, T> = {}) =>
  useQuery<SegmentSimple[], unknown, T>(queryKeys.listFavorites(), SegmentsAPI.listFavorites, options);

/**
 * Searches segments based on the provided input params.
 */
export const useQuerySegments = <T = SegmentQueryDataResponse>(
  args: GetSegmentsRequest,
  options: UseQueryOptions<SegmentQueryDataResponse, unknown, T> = {},
) => useQuery<SegmentQueryDataResponse, unknown, T>(queryKeys.query(args), () => SegmentsAPI.list(args), options);

type UseSegmentListFavoritesParams = {
  cacheKey: QueryKey;
  onSubmit: () => void;
  onFavoriteSuccess: () => void;
  onUnfavoriteSuccess: () => void;
  onError: (error: unknown) => void;
};

type HandleSegmentFavoriteToggleParams = {
  uuid: string;
  userId: number;
  newFavoriteVal: boolean;
};

/**
 * Hook to manage user interactions with "favoriting" segments.
 * Provides a handler which makes an API call and updates
 * any cached favorite data involving the given question.
 */
export const useSegmentListFavorites = ({
  cacheKey,
  onSubmit,
  onFavoriteSuccess,
  onUnfavoriteSuccess,
  onError,
}: UseSegmentListFavoritesParams) => {
  const queryClient = useQueryClient();

  const { mutate: favorite } = useMutation((formData: string) => SegmentsAPI.favorite(formData), {
    onSuccess: onFavoriteSuccess,
    onError,
  });

  const { mutate: unfavorite } = useMutation((formData: string) => SegmentsAPI.unfavorite(formData), {
    onSuccess: onUnfavoriteSuccess,
    onError,
  });

  /**
   * Handles the update of a segment's favoriteUserIds property.
   */
  const handleSegmentFavoriteToggle = ({ uuid, userId, newFavoriteVal }: HandleSegmentFavoriteToggleParams) => {
    // prepare handler for API call
    onSubmit();

    newFavoriteVal ? favorite(uuid) : unfavorite(uuid);

    // update all of the local caches with the new favorite value
    queryClient.setQueriesData<GetSegmentsResponse | undefined>(
      cacheKey,
      (oldData) =>
        oldData && {
          ...oldData,
          results: mapBy(
            (x) => x.uuid === uuid,
            (x) => ({ ...x, favoriteUserIds: newFavoriteVal ? [userId] : [] }),
            oldData.results,
          ),
        },
    );
  };

  return { handleSegmentFavoriteToggle };
};

/**
 * Mutation to Create a new segment.
 */
export const useSegmentCreate = (options: UseMutationOptions<SegmentResponse, unknown, SegmentCreateRequest> = {}) => {
  const queryClient = useQueryClient();

  return useMutation((formData) => SegmentsAPI.create(formData), {
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries(queryKeys.lists());
      options?.onSuccess?.(data, variables, context);
    },
  });
};

/**
 * Mutation to Update an existing segment.
 */
export const useSegmentUpdate = (
  id: string,
  options: UseMutationOptions<SegmentResponse, unknown, SegmentUpdateRequest> = {},
) => {
  const queryClient = useQueryClient();

  return useMutation((formData) => SegmentsAPI.update(id, formData), {
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries(queryKeys.get(id));
      queryClient.invalidateQueries(queryKeys.lists());
      options?.onSuccess?.(data, variables, context);
    },
  });
};

/**
 * Fetches a list of geo constraint values for a given segment.
 */
export const useSegmentGeos = (
  uuid: string,
  options: UseQueryOptions<ConstraintSimple[], unknown, ConstraintSimple[]> = {},
) =>
  useQuery<ConstraintSimple[], unknown, ConstraintSimple[]>(
    queryKeys.listGeos(uuid),
    () => SegmentsAPI.listGeoValues(uuid),
    options,
  );

/**
 * Fetches an AI-generated summary of a segment's demographic profile.
 *
 * Each call costs real money, so this never fetches on its own -- `enabled` must be explicitly
 * set to `true` by the caller (e.g. once an analyst clicks "Generate") in addition to there being
 * questions to summarize. `customInstructions` is part of the query key, so changing it (e.g. an
 * analyst clicking "Regenerate" with new guidance) naturally triggers a fresh fetch instead of
 * serving a stale cached summary.
 *
 * Retries and focus/mount/reconnect refetches are disabled -- react-query's defaults for these
 * would otherwise silently fire additional paid generations after the initial click (e.g. a tab
 * switch re-triggering `refetchOnWindowFocus`), undermining the whole point of gating this behind
 * `enabled`. These come after `...options` (rather than before) so a caller can't reintroduce them
 * by accident by passing its own `options`.
 */
export const useSegmentAIProfile = (
  uuid: string,
  questions: SegmentAIProfileQuestion[],
  enabled: boolean,
  customInstructions?: string,
  options: Omit<
    UseQueryOptions<AIProfileResponse, unknown, AIProfileResponse>,
    "enabled" | "retry" | "refetchOnMount" | "refetchOnReconnect" | "refetchOnWindowFocus"
  > = {},
) =>
  useQuery<AIProfileResponse, unknown, AIProfileResponse>(
    queryKeys.aiProfile(uuid, questions, customInstructions),
    () => SegmentsAPI.getAIProfileSummary(uuid, { questions, customInstructions }),
    {
      ...options,
      enabled: enabled && questions.length > 0,
      retry: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
    },
  );

/**
 * Generates draft segment constraints from a free-text prompt.
 *
 * A mutation rather than a query (unlike `useSegmentAIProfile`): it's only ever triggered by an
 * explicit submit, and each prompt is a one-off result to load into the builder, not data to
 * cache and refetch. Each call costs real money and counts against the account's generation
 * limit, so `retry` is disabled -- set after `...options` so a caller can't reintroduce retries by
 * passing its own `options`.
 *
 * A failed generation still resolves successfully with `error` set on the response (see
 * `SegmentAIConditionResponse`); only network/HTTP failures reach `onError`.
 */
export const useSegmentAIConditionBuilder = (
  options: Omit<UseMutationOptions<SegmentAIConditionResponse, unknown, SegmentAIConditionRequest>, "retry"> = {},
) =>
  useMutation((req) => SegmentsAPI.getAICondition(req), {
    ...options,
    retry: false,
  });
